import React from 'react';
import PropTypes from 'prop-types';
import { requirementsApi } from '../../services/api';
import { navClickHandler } from '../../utils/navClick';
import { approvalToneOf } from '../../utils/statusTone';
import { usePref } from '../../services/prefs';
import IdChip from '../common/IdChip';
import ItemTooltip from '../itemCard/ItemTooltip';

const PAGE_SIZE = 20;
// Fraction of the drawer body's remaining scroll distance that counts as "near
// the bottom" — matches the mockup's fixed-px threshold in spirit but scales
// with the drawer's own height instead of a magic number.
const SCROLL_THRESHOLD_PX = 120;

/**
 * #449 — docked drawer listing the requirements that carry a given link type,
 * opened from the dashboard's "Links by type" chip. Design reference:
 * docs/design/mockups/449-link-count-filter.html ("Recommended" treatment A).
 *
 * - In-drawer search filters the ALREADY-LOADED rows only (no server round
 *   trip per keystroke) — matches the mockup's `renderRows()`.
 * - Pagination is SCROLL-TRIGGERED (IntersectionObserver on a sentinel row),
 *   not a "Load more" button — the issue's explicit follow-up fix.
 * - Each row reuses the real `IdChip` (wrapped in the advanced `ItemTooltip`
 *   hover/click card) + a title link to `/item/:id` + an APPROVAL dot (F-21 /
 *   REQ-6107, #581 audit finding F3 — this used to render the retired
 *   free-text `status` attribute via `statusOf()`). Bulk-fetches
 *   `/items/approval-states` for the currently-loaded (paginated, bounded)
 *   rows only — the same batch endpoint the tree dot uses — no per-row fetch.
 */
// #558: the dashboard chip counts LINK ROWS while this drawer counts DISTINCT
// REQUIREMENTS at either end — for real data the two numbers always differ
// (owner decision 2026-08-03: keep both, make each label state its own axis).
// `linkCount` is the chip's link-row count; `total` is the fetched item count.
export default function LinkTypeDrawer({ open, linkType, initialTotal, linkCount, projectId, onClose, onNavigate }) {
  const showHumanIds = usePref('showHumanIds');
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(initialTotal || 0);
  const [hasMore, setHasMore] = React.useState(false);
  const [nextOffset, setNextOffset] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState('');
  // F-21 / REQ-6107 (#581, F3): approval-rollup dot for the currently-loaded rows —
  // best-effort, never blocks the row list (matches the tree's own bulk fetch).
  const [approvalStates, setApprovalStates] = React.useState({});

  const seq = React.useRef(0);
  const bodyRef = React.useRef(null);
  const sentinelRef = React.useRef(null);

  // `initialTotal` is the caller's already-known count, used ONLY to seed the header
  // while the real page is in flight — it is not a reason to re-open and re-fetch the
  // drawer, which is what listing it would mean (the parent's dashboard aggregate
  // refreshes on its own cadence). Read the latest value through a ref at seed time.
  const initialTotalRef = React.useRef(initialTotal);
  React.useEffect(() => { initialTotalRef.current = initialTotal; }, [initialTotal]);

  // Fresh load whenever a NEW link type is opened. `open` false -> no-op (the
  // drawer stays mounted so its close transition can play, but doesn't fetch).
  React.useEffect(() => {
    if (!open || !linkType) return undefined;
    let live = true;
    const mySeq = (seq.current += 1);
    setItems([]);
    setApprovalStates({});
    setTotal(initialTotalRef.current || 0);
    setHasMore(false);
    setNextOffset(0);
    setSearch('');
    setError(null);
    setLoading(true);
    requirementsApi.getItemsByLinkType(linkType, { projectId, limit: PAGE_SIZE, offset: 0 })
      .then(({ items: rows, page }) => {
        if (!live || seq.current !== mySeq) return;
        setItems(rows);
        setTotal((page && page.total) ?? rows.length);
        setHasMore(!!(page && page.has_more));
        setNextOffset((page && page.next_offset) ?? rows.length);
      })
      .catch((e) => { if (live && seq.current === mySeq) setError(e.message || 'Failed to load'); })
      .finally(() => { if (live && seq.current === mySeq) setLoading(false); });
    return () => { live = false; };
  }, [open, linkType, projectId]);

  const loadMore = React.useCallback(() => {
    if (loading || loadingMore || !hasMore || search.trim()) return;
    const mySeq = seq.current;
    setLoadingMore(true);
    requirementsApi.getItemsByLinkType(linkType, { projectId, limit: PAGE_SIZE, offset: nextOffset })
      .then(({ items: rows, page }) => {
        if (seq.current !== mySeq) return;
        setItems((prev) => [...prev, ...rows]);
        setHasMore(!!(page && page.has_more));
        setNextOffset((page && page.next_offset) ?? (nextOffset + rows.length));
      })
      .catch(() => { /* a failed "load more" leaves the loaded rows intact; the
        sentinel just stops advancing — the user can scroll/retry by reopening */ })
      .finally(() => { if (seq.current === mySeq) setLoadingMore(false); });
  }, [linkType, projectId, nextOffset, hasMore, loading, loadingMore, search]);

  // Scroll-triggered pagination (#449 fix — replaces a "Load more" button):
  // an IntersectionObserver on the sentinel row fires loadMore() once it nears
  // the drawer body's viewport, scoped to the body itself (not the page) as the
  // `root` so it only reacts to scrolling INSIDE the drawer.
  React.useEffect(() => {
    if (!open || !hasMore || search.trim()) return undefined;
    const root = bodyRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) loadMore(); },
      { root, rootMargin: `0px 0px ${SCROLL_THRESHOLD_PX}px 0px` },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [open, hasMore, search, loadMore, items.length]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // F-21 / REQ-6107 (#581, F3): bulk-fetch the approval rollup for whatever rows
  // are currently loaded (bounded — grows only via explicit scroll pagination,
  // never the whole result set at once). Best-effort: a failure just leaves rows
  // without a dot, same convention as the tree's own bulk fetch.
  const loadedIdsKey = React.useMemo(
    () => items.map((it) => it.id).filter(Boolean).join(','),
    [items],
  );
  React.useEffect(() => {
    let live = true;
    if (!loadedIdsKey) return undefined;
    requirementsApi.getApprovalStates(loadedIdsKey)
      .then((res) => { if (live) setApprovalStates((prev) => ({ ...prev, ...res })); })
      .catch(() => { /* best-effort — rows simply stay without a dot */ });
    return () => { live = false; };
  }, [loadedIdsKey]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((it) => (
      String(it.display_id || '').toLowerCase().includes(q)
      || String(it.id || '').toLowerCase().includes(q)
      || String(it.title || '').toLowerCase().includes(q)
    ))
    : items;

  return (
    <>
      {/* #793 H: the scrim is a decorative click-catcher, not a control — it carries
          no content and must never become a tab stop that announces nothing. The
          keyboard dismissal path is Escape (the effect above) plus the visible Close
          button in the drawer head, so `aria-hidden` is the accurate annotation here
          rather than bolting a phantom role/tabIndex onto a transparent overlay. */}
      <div className={`lt-scrim${open ? ' on' : ''}`} onClick={onClose} aria-hidden="true" data-testid="link-type-drawer-scrim" />
      <div
        className={`lt-drawer${open ? ' on' : ''}`}
        role="dialog"
        aria-label={linkType ? `Requirements with a ${linkType} link` : 'Requirements'}
        aria-hidden={!open}
        data-testid="link-type-drawer"
      >
        <div className="lt-drawer-head">
          <div className="row1">
            <div className="lt-drawer-title">
              <span className="lt-type">{linkType}</span> · <span data-testid="link-type-drawer-total">{total}</span> requirement{total === 1 ? '' : 's'} involved
            </div>
            <button type="button" className="icon-btn" title="Close" aria-label="Close" onClick={onClose}>✕</button>
          </div>
          <div className="lt-drawer-sub" data-testid="link-type-drawer-axes">
            {linkCount != null ? (
              <>
                <b>{total}</b> requirement{total === 1 ? '' : 's'} involved in{' '}
                <b>{linkCount}</b> <span className="mono">{linkType}</span> link{linkCount === 1 ? '' : 's'} —
                the dashboard chip counts links; this list counts the requirements at either end.
              </>
            ) : (
              <>Requirements involved in a <span className="mono">{linkType}</span> link.</>
            )}
          </div>
          <div className="lt-drawer-search">
            <span className="sic" aria-hidden="true">🔍</span>
            <input
              className="input"
              placeholder="Filter by id or title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Filter the loaded requirements"
              data-testid="link-type-drawer-search"
            />
          </div>
        </div>

        <div className="lt-drawer-body" ref={bodyRef} data-testid="link-type-drawer-body">
          {loading && (
            <div className="mloading" style={{ padding: '12px 8px' }}><span className="spin" /> Loading…</div>
          )}
          {!loading && error && (
            <div className="t-body" style={{ color: 'var(--error)', padding: '12px 8px' }}>{error}</div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="lt-drawer-empty" data-testid="link-type-drawer-empty">
              <div className="ic" aria-hidden="true">🔎</div>
              {q ? (
                <>
                  <div className="t-body">No matches for &ldquo;{search}&rdquo;</div>
                  <div className="t-label muted3" style={{ marginTop: 4 }}>
                    among the {items.length} loaded requirement{items.length === 1 ? '' : 's'} with a <span className="mono">{linkType}</span> link
                  </div>
                  <button type="button" className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => setSearch('')}>
                    Clear search
                  </button>
                </>
              ) : (
                <div className="t-body">None — no requirements carry a <span className="mono">{linkType}</span> link.</div>
              )}
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <>
              {filtered.map((it) => {
                const rec = approvalStates[it.id];
                // F-11 / REQ-6101: `stale_state` keeps a lapsed REJECTION from being
                // labelled "Approved · stale" on this row.
                const tone = rec ? approvalToneOf(rec.state, rec.stale_state) : null;
                // F-61 / REQ-6119 (#606, D-10, #629): same annotation as the tree dot /
                // Breakdown row — the state itself is unchanged (the approval STANDS).
                const toneLabel = tone && rec.reviewer_inactive ? `${tone.label} · user inactive` : (tone && tone.label);
                const title = it.title || 'Untitled';
                return (
                  <div className="lt-drow" key={it.id}>
                    {/* F-21 / REQ-6107 (#581, F2): seed the hover mini with the approval
                        rollup this drawer has ALREADY bulk-fetched, so the mini renders
                        the dot without a per-hover fetch of its own (ItemCardLayer). */}
                    <ItemTooltip item={{ id: it.id, display_id: it.display_id, title, type: it.type, status: it.attributes?.status, approval_state: rec?.state, approval_stale_state: rec?.stale_state, approval_reviewer_inactive: rec?.reviewer_inactive }}>
                      <IdChip id={it.id} displayId={it.display_id} showHumanIds={showHumanIds} title={title} disableTooltip />
                    </ItemTooltip>
                    <a
                      className="lt-drow-title"
                      href={`/item/${it.id}`}
                      onClick={navClickHandler(() => onNavigate(it.id))}
                    >
                      {title}
                    </a>
                    {/* F-21/F-23 (#581 F3, #607): approval rollup, not the retired
                        status attribute; glyph + aria-label so it isn't colour-only. */}
                    {tone && (
                      <span className="lt-drow-status">
                        <span
                          className={`dot ${tone.className}`}
                          role="img"
                          aria-label={toneLabel}
                          data-testid={`link-drawer-approval-dot-${it.id}`}
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10, fontSize: 7, fontWeight: 900, color: '#fff' }}
                        >
                          {tone.glyph}
                        </span>
                        {toneLabel}
                      </span>
                    )}
                  </div>
                );
              })}
              {!q && hasMore && (
                <div className="lt-sentinel" ref={sentinelRef} data-testid="link-type-drawer-sentinel">
                  Loading more…
                </div>
              )}
            </>
          )}
        </div>

        <div className="lt-drawer-foot">
          <span className="cnt">
            {q
              ? `Matching ${filtered.length} of ${items.length} loaded`
              : `Showing ${items.length} of ${total}`}
          </span>
        </div>
      </div>
    </>
  );
}

LinkTypeDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  linkType: PropTypes.string,
  initialTotal: PropTypes.number,
  projectId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};
